// DOCREFUSAL (2026-10-21): document messages in the viewer's language, and the server's real reason.
//
// WHY A SMALL HELPER AND NOT useTranslation's t() DIRECTLY. The archive-and-replace dialog lives in
// ui.js, a shared module imported by screens whose tests do not initialise i18n; there t() returns
// the key itself, and initialising the shared instance from ui.js would change what every one of
// those screens renders. This reads the SAME locale files (en.json, fr.json) for the language
// i18n reports, so the wording is still entirely in the locale files, in English and French.
//
// refusalMessage(err, lang, fallbackKey): the message to show for a failed document request.
//   1. the server's reason code (X-Refusal-Reason header) -> docRefusal.<code> in the viewer's
//      language;
//   2. otherwise the server's own `detail` text, never replaced by a generic sentence;
//   3. otherwise (no response at all) the caller's fallback key.
import axios from 'axios';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import en from './locales/en.json';
import fr from './locales/fr.json';

const DICTS = { en, fr };

const lookup = (dict, key) => key.split('.').reduce((a, k) => (a == null ? undefined : a[k]), dict);

export const langOf = (lang) => (String(lang || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en');

export function docText(key, lang = 'en', vars = {}) {
  let s = lookup(DICTS[langOf(lang)], key);
  if (typeof s !== 'string') s = lookup(en, key);
  if (typeof s !== 'string') return key;
  return s.replace(/\{\{\s*(\w+)\s*\}\}/g, (_m, k) => (vars[k] == null ? '' : String(vars[k])));
}

// The language i18n is set to, or English when i18n is not available (some tests mock it away).
export function useDocLang() {
  let lang = 'en';
  try {
    const r = useTranslation();
    lang = (r && r.i18n && r.i18n.language) || 'en';
  } catch (e) {
    lang = 'en';
  }
  return lang;
}

export function refusalCode(err) {
  const headers = (err && err.response && err.response.headers) || {};
  return headers['x-refusal-reason'] || headers['X-Refusal-Reason'] || null;
}

export function refusalMessage(err, lang, fallbackKey) {
  const code = refusalCode(err);
  if (code) {
    const s = lookup(DICTS[langOf(lang)], `docRefusal.${code}`);
    if (typeof s === 'string') return s;
  }
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return docText(fallbackKey, lang);
}

// DOCREFUSAL point 10: a check the BROWSER decides still leaves a trace. Fire and forget: the
// instant message is already on screen, and a failed report must never get in its way. Only the
// fixed values go out; the server takes the employee from the session.
const BROWSER_REASONS = new Set(['file_type_blocked', 'file_too_large', 'file_empty',
  // UPLOADREFUSE (2026-10-22): the upload forms' own checks
  'no_file', 'no_document_type', 'missing_passport_number', 'missing_national_id', 'missing_expiry_date',
  'missing_address_line1', 'too_many_files']);

// UPLOADREFUSE: action is 'upload' for the upload forms; the archive-and-replace dialog passes
// 'archive', which the server takes as its default, so that dialog's request is unchanged.
export function reportBrowserRefusal(reason, documentType, action = 'upload') {
  if (!BROWSER_REASONS.has(reason)) return;
  try {
    const body = { reason, document_type: documentType || '' };
    if (action !== 'archive') body.action = action;
    const call = axios.post('/api/documents/refusals', body);
    if (call && typeof call.catch === 'function') call.catch(() => {});
  } catch (e) {
    // nothing to do: the refusal is still shown to the user
  }
}

// UPLOADREFUSE (2026-10-22): the viewer's language outside a component render (in an event handler),
// read from the i18next instance without initialising it; English when i18n is not set up.
export function currentLang() {
  try { return (i18next && i18next.language) || 'en'; } catch (e) { return 'en'; }
}
